/**
 * Workspace capability model — the single source of truth for "what may a
 * member of this workspace do", shared by the API (enforcement) and the web
 * (hiding controls) so the two can never drift.
 *
 * A workspace membership has a role (`owner | member`). `owner` holds every
 * capability. `member` holds NO standalone capabilities (deny-by-default): a
 * plain member can read within the workspace (reads are not capability-gated)
 * but gets no write power from the org role alone. Project access and its
 * capabilities come from a per-project grant carrying a project role
 * (`viewer | contributor`; see `PROJECT_ROLE_CAPABILITIES` and the later
 * `resolveCapabilities`), NOT from workspace membership.
 *
 * There is no platform role in this policy. Platform operators live on the
 * separate ops plane (its own process and `Operator` table), never in the
 * tenant `User` table, so inside a workspace every user sees and acts only per
 * their membership role + grants. This module only concerns workspace roles.
 *
 * The `resource` argument on `can()` is the seam for a future per-project layer
 * (grant a member access to specific projects): today the policy is role-only
 * and ignores it, so per-project grants can later feed the same call without
 * changing capability names or any enforcement call-site.
 */

export type WorkspaceRole = 'owner' | 'member';

export type Capability =
  // Issue board — held by the PROJECT role `contributor` (via a grant), not by
  // the org `member` role, which holds nothing on its own.
  | 'issue:create'
  | 'issue:edit'
  | 'issue:approve'
  | 'issue:close'
  | 'issue:comment'
  | 'chat:post'
  // Owner-only: hard-deleting an issue is destructive (contributors only
  // close/dismiss).
  | 'issue:delete'
  // Owner-only workspace management.
  | 'project:manage'
  | 'loop:manage'
  | 'prototype:manage'
  // Keep a prototype's live preview running: the "In production" switch and
  // waking a sleeping prototype. Held by an owner and a project `contributor`.
  | 'prototype:operate'
  | 'pr:merge'
  | 'run:trigger'
  | 'run:stop'
  // Owner-only: mint/revoke a project's Integration API tokens (external
  // sites submitting requests). A token is a standing credential for the
  // project's board, so no project grant ever confers it.
  | 'integrations:manage'
  | 'workspace:manage';

/**
 * The issue-board capabilities. Held by the PROJECT role `contributor`, NOT by
 * the org role `member` — an org member has no standalone capabilities; project
 * access and its capabilities come from a grant (see resolveCapabilities).
 */
export const ISSUE_BOARD_CAPABILITIES: readonly Capability[] = [
  'issue:create',
  'issue:edit',
  'issue:approve',
  'issue:close',
  'issue:comment',
  'chat:post',
];

/** Everything an owner can do — the whole capability set. */
export const OWNER_CAPABILITIES: readonly Capability[] = [
  ...ISSUE_BOARD_CAPABILITIES,
  'issue:delete',
  'project:manage',
  'loop:manage',
  'prototype:manage',
  'prototype:operate',
  'pr:merge',
  'run:trigger',
  'run:stop',
  'integrations:manage',
  'workspace:manage',
];

/** Alias: the complete capability universe (owner holds all of it). */
export const ALL_CAPABILITIES = OWNER_CAPABILITIES;

/**
 * Org member holds NOTHING on its own (deny-by-default). Kept exported for the
 * ROLE_CAPABILITIES map and any legacy reader.
 */
export const MEMBER_CAPABILITIES: readonly Capability[] = [];

export const ROLE_CAPABILITIES: Record<WorkspaceRole, readonly Capability[]> = {
  owner: OWNER_CAPABILITIES,
  member: MEMBER_CAPABILITIES,
};

export type CapabilityScope = 'org' | 'project';

/** Every capability's scope. Exhaustive by construction (satisfies-checked). */
export const CAPABILITY_SCOPE = {
  'issue:create': 'project',
  'issue:edit': 'project',
  'issue:approve': 'project',
  'issue:close': 'project',
  'issue:comment': 'project',
  'chat:post': 'project',
  'issue:delete': 'project',
  'project:manage': 'project',
  'loop:manage': 'project',
  'prototype:manage': 'project',
  'prototype:operate': 'project',
  'pr:merge': 'project',
  'run:trigger': 'project',
  'run:stop': 'project',
  'integrations:manage': 'project',
  'workspace:manage': 'org',
} satisfies Record<Capability, CapabilityScope>;

/** Project-scope roles carried on a grant. */
export type ProjectRole = 'viewer' | 'contributor';

/**
 * Project role → capabilities. `viewer` holds no WRITE caps: read access is
 * conferred by the grant's existence (reads are not capability-gated), so a
 * viewer needs no read capability. `contributor` adds the issue-board caps
 * and `prototype:operate` (keep the preview awake / wake it).
 */
export const PROJECT_ROLE_CAPABILITIES: Record<ProjectRole, readonly Capability[]> = {
  viewer: [],
  contributor: [...ISSUE_BOARD_CAPABILITIES, 'prototype:operate'],
};

/** Optional resource context — reserved for a future per-project grant layer. */
export interface CapabilityResource {
  projectId?: string;
}

/**
 * Whether a workspace role may perform a capability. Fails closed: an unknown
 * role or a capability not granted to the role returns false. `resource` is
 * accepted but ignored today (the per-project seam).
 */
export function can(
  role: WorkspaceRole,
  capability: Capability,
  _resource?: CapabilityResource,
): boolean {
  return ROLE_CAPABILITIES[role]?.includes(capability) ?? false;
}

export interface ProjectGrant {
  projectId: string;
  role: ProjectRole;
}

/**
 * Everything the policy needs to decide, resolved from the request once:
 * the org role in the addressed workspace and the caller's project grants in
 * that workspace.
 */
export interface AccessContext {
  orgRole: WorkspaceRole;
  grants: readonly ProjectGrant[];
}

/**
 * The context for a TRUSTED INTERNAL caller — a scheduler, the run pipeline,
 * the reactive dispatcher — acting as the platform rather than on behalf of a
 * signed-in user. It holds every capability, so passing it BYPASSES the policy.
 *
 * It exists so that "no user context" has to be written down rather than
 * defaulted into. Authorization parameters are deliberately REQUIRED across the
 * services: an optional context silently resolves to "no filtering", which is
 * how a whole API surface once ended up unscoped. With this sentinel the
 * bypass is explicit, greppable, and reviewable — every use should be a system
 * path, and any use on a request path is a bug you can see.
 *
 * NEVER derive this from a request. Build request contexts with
 * `buildAccessContext` instead.
 */
export const SYSTEM_ACCESS_CONTEXT: AccessContext = Object.freeze({
  orgRole: 'owner',
  grants: Object.freeze([]) as readonly ProjectGrant[],
});

/**
 * The single resolution path every authorization decision flows through.
 * Returns the effective capability set for the given resource.
 */
export function resolveCapabilities(
  ctx: AccessContext,
  resource?: CapabilityResource,
): Set<Capability> {
  const caps = new Set<Capability>(ROLE_CAPABILITIES[ctx.orgRole] ?? []);

  if (resource?.projectId) {
    if (ctx.orgRole === 'owner') {
      for (const c of ALL_CAPABILITIES) {
        if (CAPABILITY_SCOPE[c] === 'project') caps.add(c);
      }
    } else {
      const grant = ctx.grants.find((g) => g.projectId === resource.projectId);
      if (grant) {
        for (const c of PROJECT_ROLE_CAPABILITIES[grant.role]) caps.add(c);
      }
    }
  }
  return caps;
}

/** Capability check over resolveCapabilities. */
export function canDo(
  ctx: AccessContext,
  capability: Capability,
  resource?: CapabilityResource,
): boolean {
  return resolveCapabilities(ctx, resource).has(capability);
}

/**
 * Access check: may this user see/enter this project at all? A workspace owner
 * sees every project in it; everyone else needs a grant on that project.
 */
export function canAccessProject(ctx: AccessContext, projectId: string): boolean {
  if (ctx.orgRole === 'owner') return true;
  return ctx.grants.some((g) => g.projectId === projectId);
}
